
import { useNavigate } from 'react-router-dom';
import React from 'react';

interface NearbyState {
  name: string;
  slug: string;
  path: string;
  topRate: string;
  abbreviation: string;
}

interface StateNearbyCitiesProps {
  stateName: string;
  region: string;
  nearbyStates: NearbyState[];
}

/**
 * Component that renders a grid of nearby states with quick navigation links.
 * Handles empty `nearbyStates` gracefully and ensures navigation works
 * both with normal links and programmatic routing.
 */
const StateNearbyCities: React.FC<StateNearbyCitiesProps> = ({
  stateName,
  region,
  nearbyStates,
}) => {
  const navigate = useNavigate();

  // If there are no nearby states, render nothing.
  if (!nearbyStates || nearbyStates.length === 0) return null;

  /**
   * Safely parse a percentage string (e.g., "4.5%") into a numeric value.
   * Returns `null` if parsing fails.
   */
  const parseRate = (rate: string): number | null => {
    const num = parseFloat(rate.replace('%', ''));
    return isNaN(num) ? null : num;
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Other States in {region}
        </h2>
        <p className="text-gray-600 mb-8">
          Compare tax rates and take-home pay in nearby {region} states
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {nearbyStates.map((state) => {
            const rateValue = parseRate(state.topRate);
            const badgeClasses = (() => {
              if (state.topRate === '0%') {
                return 'bg-emerald-100 text-emerald-700';
              }
              if (rateValue !== null && rateValue <= 5) {
                return 'bg-teal-100 text-teal-700';
              }
              return 'bg-amber-100 text-amber-700';
            })();

            return (
              <a
                key={state.slug}
                href={state.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(state.path);
                }}
                className="group bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-400 hover:shadow-lg transition-all cursor-pointer transform hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg group-hover:bg-teal-500 transition-colors">
                    <span className="text-sm font-bold text-teal-700 group-hover:text-white transition-colors">
                      {state.abbreviation}
                    </span>
                  </div>
                  <span
                    className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${badgeClasses}`}
                  >
                    {state.topRate}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  {state.name}
                </h3>

                <div className="flex items-center text-teal-600 text-sm font-medium group-hover:translate-x-1 transition-transform">
                  Calculate <i className="ri-arrow-right-line ml-1"></i>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StateNearbyCities;
